import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { getLocationPlacesOfService } from "../../services/place-of-service";
import { handleApiError } from "../../utils/handleApiError";

// Place of Service dropdown for onboarding and Patient Settings.
// Lists only the active places of service of `locationId` (the location's
// own entry first, as the backend orders them). With `selectDefault` it
// picks the location's own entry while `value` is empty (onboarding);
// otherwise an empty value shows the placeholder.
// `currentPlace` is the patient's saved { id, name } - if it has since been
// switched off it isn't in the active list, so it's shown disabled to keep
// the saved value visible without letting anyone pick it again.
const PlaceOfServiceSelect = ({
  locationId,
  value,
  onChange,
  currentPlace,
  selectDefault = false,
  disabled = false,
}) => {
  const { data, isLoading } = useQuery(
    ["places-of-service", locationId, "active"],
    () => getLocationPlacesOfService(locationId, true),
    {
      enabled: Boolean(locationId),
      onError: handleApiError,
    }
  );
  const activePlaces = data?.places_of_service || [];

  useEffect(() => {
    if (!selectDefault || value || !activePlaces.length) return;
    const locationDefault =
      activePlaces.find(place => place.is_location_default) || activePlaces[0];
    onChange(locationDefault.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDefault, value, data]);

  const isCurrentSwitchedOff =
    currentPlace?.id && !activePlaces.some(place => place.id === currentPlace.id);
  const options = isCurrentSwitchedOff
    ? [...activePlaces, { ...currentPlace, switchedOff: true }]
    : activePlaces;

  const selected = options.find(place => place.id === value);

  return (
    <Select
      onChange={onChange}
      value={value || ""}
      disabled={disabled || isLoading}
    >
      <SelectTrigger>
        <SelectValue placeholder="Select your place of service">
          {isLoading
            ? "Loading places of service..."
            : selected?.name || "Select your place of service"}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map(place => (
          <SelectItem
            key={place.id}
            value={place.id}
            disabled={Boolean(place.switchedOff)}
          >
            {place.switchedOff ? `${place.name} (switched off)` : place.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default PlaceOfServiceSelect;
